import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

// Recherche globale unique : un seul champ qui interroge clients, reprises,
// interventions, devis et tickets de caisse en une fois (voir
// routers/search_routes.py), plutôt que de devoir chercher module par
// module. Le debounce (250ms) évite de spammer le serveur à chaque frappe.
export function GlobalSearch() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setCategories([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/global", { params: { q: query.trim() } });
        setCategories(data.categories || []);
        setOpen(true);
      } catch {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const goTo = (link) => {
    setOpen(false);
    setQuery("");
    setExpanded(false);
    navigate(link);
  };

  const close = () => {
    setExpanded(false);
    setQuery("");
    setOpen(false);
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        data-testid="global-search-open-button"
        className="p-2.5 rounded-lg border border-border hover:bg-accent transition-colors duration-200 shrink-0"
        aria-label="Recherche globale"
      >
        <Search size={18} />
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative w-48 sm:w-72">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          autoFocus
          data-testid="global-search-input"
          placeholder="Rechercher un client, un ticket, une reprise..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => categories.length > 0 && setOpen(true)}
          className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        {loading ? (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
        ) : (
          <button onClick={close} data-testid="global-search-close-button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-card shadow-lg max-h-[70vh] overflow-y-auto z-50" data-testid="global-search-results">
          {categories.length === 0 ? (
            <p className="text-sm text-muted-foreground p-3">Aucun résultat pour « {query} ».</p>
          ) : (
            categories.map((cat) => (
              <div key={cat.key} className="border-b border-border last:border-0">
                <p className="text-xs font-semibold text-muted-foreground px-3 pt-2 pb-1">{cat.label}</p>
                {cat.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => goTo(item.link)}
                    data-testid={`global-search-result-${cat.key}-${item.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-accent transition-colors duration-150 flex items-center justify-between gap-2"
                  >
                    <span className="text-sm truncate">{item.label}</span>
                    {item.sublabel && <span className="text-xs text-muted-foreground shrink-0">{item.sublabel}</span>}
                  </button>
                ))}
                {cat.truncated && (
                  <p className="text-xs text-muted-foreground px-3 pb-2">D'autres résultats existent — affinez votre recherche.</p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
